import { ArrowForwardIosSharp, Edit } from '@mui/icons-material';
import { Grid, Typography, Paper, Accordion, AccordionDetails, AccordionSummary, Button, Skeleton, Stack, Alert } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { Sending } from 'src/modules/invoice/models/invoiceTemplate';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Colors } from 'src/shared/components/styles/colors';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { AdminInvoiceTemplateSkeleton } from './adminInvoiceTemplateSkeleton';
import { CreateInvoiceTemplatePreviewRequest } from '../models/createInvoiceTemplatePreviewRequest';
import { PreviewInvoiceTemplateDownloader } from './previewInvoiceTemplateDownloader';

type RenderInvoiceTemplateSendingProps = {
  loading: boolean;
  setLoading: (pdfLoader: boolean) => void;
  sendingItem: Sending;
  index: number;
};

export const RenderInvoiceTemplateSending = (props: RenderInvoiceTemplateSendingProps) => {
  const { loading, setLoading, sendingItem, index } = props;
  const [edit, setEdit] = useState<boolean>(false);

  const dataToPdf: CreateInvoiceTemplatePreviewRequest = {
    templateSendingsId: sendingItem.templateSendings_id,
  };

  // Konfiguration för vilken försändelsetyp som visar fakturablocket.
  const sendingConfigByType: Record<number, { titleKey: string; showInvoice: boolean }> = {
    [InvoiceSendingTypeIntegerEnum.Email]: {
      titleKey: 'invoice:adminInvoiceTemplatePage.emailSending',
      showInvoice: false,
    },
    [InvoiceSendingTypeIntegerEnum.Main]: {
      titleKey: 'invoice:adminInvoiceTemplatePage.mainSending',
      showInvoice: true,
    },
    [InvoiceSendingTypeIntegerEnum.Reminder1]: {
      titleKey: 'invoice:adminInvoiceTemplatePage.reminderOne',
      showInvoice: true,
    },
    [InvoiceSendingTypeIntegerEnum.Reminder2]: {
      titleKey: 'invoice:adminInvoiceTemplatePage.reminderTwo',
      showInvoice: true,
    },
  };

  const sendingConfig = sendingConfigByType[sendingItem.sendingType] ?? {
    titleKey: 'invoice:adminInvoiceTemplatePage.emailSending',
    showInvoice: false,
  };

  const sendingTitle = t(sendingConfig.titleKey);

  // Hjälpmetod för att bygga formfältens namn.
  const buildFieldName = (field: 'emailHeading' | 'emailText' | 'defaultHeading' | 'defaultText') => `sendings[${index}].${field}`;

  // Gemensam etikett för blockrubrikerna.
  const renderSectionLabel = (kind: 'email' | 'invoice') => {
    const labelKey = kind === 'email' ? 'invoice:adminInvoiceTemplatePage.emailContent' : 'invoice:adminInvoiceTemplatePage.invoiceContent';
    const marginBottom = kind === 'invoice' ? 1 : 0;

    return (
      <Typography sx={{ fontWeight: 500, color: 'text.secondary', mb: marginBottom }}>
        {sendingTitle} - {t(labelKey)}
      </Typography>
    );
  };

  // Återanvänd samma knappar för förhandsgranskning och redigering.
  const renderActionButtons = () => (
    <Grid container mt={1} mb={3} justifyContent='space-between' alignItems='center'>
      <Grid item xs='auto' mb={{ xs: 1, sm: 0 }}>
        <Button variant='outlined' disabled={loading} onClick={() => PreviewInvoiceTemplateDownloader({ dataToPdf, loadingCallback: setLoading })}>
          {t('invoice:createMemberInvoice.previewInvoice')}
        </Button>
      </Grid>
      <Grid item xs='auto'>
        <Button
          size='medium'
          variant='outlined'
          endIcon={<Edit fontSize='small' />}
          sx={{ mr: 2 }}
          disabled={edit || loading}
          onClick={() => setEdit(true)}
        >
          {t('common:button.edit')}
        </Button>
      </Grid>
    </Grid>
  );

  const renderEmailSection = () => (
    <Paper
      sx={{
        border: '2px solid, lightGrey',
        background: '#F5F5F5',
        boxShadow: 4,
        marginBottom: 4,
      }}
    >
      <Grid container px={2} mt={1}>
        <Grid item xs={12} mt={1} mb={1}>
          {renderSectionLabel('email')}
        </Grid>
        <Grid item xs={12} mt={2} mb={2}>
          <ControlledTextField
            sx={{ backgroundColor: Colors.White }}
            disabled={!edit}
            name={buildFieldName('emailHeading')}
            label={t('invoice:createMemberInvoice.heading')}
          />
        </Grid>
        <Grid item xs={12} mb={4}>
          <LoadingOverlay loading={loading} />
          <ControlledTextEditor adminInvoice disabled={!edit} rows={30} name={buildFieldName('emailText')} />
        </Grid>
        <Grid item xs={12}>
          {renderActionButtons()}
        </Grid>
      </Grid>
    </Paper>
  );

  const renderInvoiceSection = () => {
    if (!sendingConfig.showInvoice) {
      return null;
    }

    return (
      <Paper
        sx={{
          border: '2px solid, lightGrey',
          background: '#F5F5F5',
          boxShadow: 4,
          marginBottom: 4,
        }}
      >
        <Grid container px={2} mt={1}>
          <Grid item xs={12} mt={1} mb={1}>
            {renderSectionLabel('invoice')}
          </Grid>
          <Grid item xs={12} mb={1}>
            <AdminInvoiceTemplateSkeleton />
          </Grid>
          <Grid item xs={12} mt={2} mb={3}>
            <ControlledTextField
              sx={{ backgroundColor: Colors.White }}
              disabled={!edit}
              name={buildFieldName('defaultHeading')}
              label={t('invoice:createMemberInvoice.heading')}
            />
          </Grid>
          <>
            <Grid item xs={9} mb={2}>
              <Typography variant='body2' color='textSecondary'>
                {t('invoice:createMemberInvoice.memberNumber')}
              </Typography>
              <Skeleton variant='text' animation={false} sx={{ height: '20px', width: '90%' }} />
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' color='textSecondary'>
                {t('invoice:createMemberInvoice.fee')}
              </Typography>
              <Skeleton variant='text' animation='wave' sx={{ height: '20px', width: '95%' }} />
            </Grid>
          </>
          <Grid item xs={12} mb={2}>
            <LoadingOverlay loading={loading} />
            <ControlledTextEditor adminInvoice disabled={!edit} rows={30} name={buildFieldName('defaultText')} />
          </Grid>
          <Grid item xs={12}>
            {renderActionButtons()}
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <>
      <Accordion disableGutters elevation={0}>
        <AccordionSummary
          expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
          aria-controls={`panel-${sendingItem.templateSendings_id}-content`}
          id={`panel-${sendingItem.templateSendings_id}-header`}
        >
          <Typography sx={{ fontWeight: 500 }}>{sendingTitle}</Typography>
          {(sendingItem.notReady || sendingItem.textApproved === false) && (
            <Stack direction='row' spacing={1} ml={1.5} sx={{ '& .MuiChip-filled ': { borderRadius: 1 } }}>
              <CustomChip label={t('invoice:adminInvoiceTemplatePage.notApproved')} color='error' size='small' />
            </Stack>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Grid container>
            {(sendingItem.notReady || sendingItem.textApproved === false) && (
              <Alert sx={{ marginLeft: 5 }} severity='error'>
                {t('invoice:adminInvoiceTemplatePage.invoiceTemplateNotApproved')}
              </Alert>
            )}
            <Grid item xs={12} px={5}>
              <Grid container mt={1}>
                {renderEmailSection()}
                {renderInvoiceSection()}
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
